/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import {
    TextBlock,
    Stack,
    DividerNew as Divider,
    DisplayHtmlContent,
    FlexRow,
    FlexCol,
    Heading,
} from 'sarsaparilla';

import {
    AnchorPageWithSidebarLayout,
    AnchorPageSection,
} from 'shared-ui/components/AnchorPageWithSidebarLayout';
import PermitVenueDates from './PermitVenueDates';

export function ContentTextInfo({ heading, textContent, dividerUp }) {
    return textContent ? (
        <>
            {dividerUp && <Divider />}
            <AnchorPageSection heading={heading}>
                <TextBlock>
                    <DisplayHtmlContent html={textContent} />
                </TextBlock>
            </AnchorPageSection>
            {!dividerUp && <Divider />}
        </>
    ) : null;
}

ContentTextInfo.propTypes = {
    heading: PropTypes.string,
    textContent: PropTypes.string,
    dividerUp: PropTypes.bool,
};

export default function FacilityInfoTab({
    importantDates,
    addresses,
    permitDescription = {},
    latitude,
    longitude,
    phone,
    map,
    video,
}) {
    const {
        lottery_information: lotteryInformation,
        season_information: seasonInformation,
        need_to_know_permit: needToKnowItems,
        quota_details: quotaDetails,
        fee_policy: feePolicy,
        cancellation_policy: cancellationPolicy,
        no_show_policy: noShowPolicy,
        discounts,
        driving_directions: drivingDirections,
    } = permitDescription;

    const address = addresses?.[0] ?? {};
    let address3;
    if (address.city && address.state && address.zip_code) {
        address3 = `${address.city}, ${address.state} ${address.zip_code}`;
    }

    return (
        <AnchorPageWithSidebarLayout className="te-facility-fees-tab">
            <Stack space="lg" className="mb-5 rec-tabs-wrap">
                <ContentTextInfo
                    heading="Permit and Season Information"
                    textContent={seasonInformation}
                />
                {!isEmpty(importantDates) && (
                    <>
                        <AnchorPageSection heading="Important Dates">
                            <PermitVenueDates
                                permit={{ important_dates: importantDates }}
                                hideTitle
                            />
                        </AnchorPageSection>
                        <Divider />
                    </>
                )}
                <ContentTextInfo
                    heading="Lottery Information"
                    textContent={lotteryInformation}
                />
                <ContentTextInfo
                    heading="How Does the Quota Work for this Permit?"
                    textContent={quotaDetails}
                />
                <ContentTextInfo heading="Need to Know" textContent={needToKnowItems} />

                <AnchorPageSection heading="Fees and Cancellations">
                    <Stack>
                        <section>
                            <Heading className="mb-2" headingLevel={3} appearance="h6">
                                Fee Policies
                            </Heading>
                            <TextBlock>
                                <DisplayHtmlContent html={feePolicy} />
                            </TextBlock>
                        </section>
                        <section>
                            <Heading className="mb-2" headingLevel={3} appearance="h6">
                                Cancellation & Change Policies
                            </Heading>
                            <TextBlock>
                                <DisplayHtmlContent html={cancellationPolicy} />
                            </TextBlock>
                        </section>
                        {noShowPolicy && (
                            <section>
                                <Heading
                                    className="mb-2"
                                    headingLevel={3}
                                    appearance="h6"
                                >
                                    No Show Policies
                                </Heading>
                                <TextBlock>
                                    <DisplayHtmlContent html={noShowPolicy} />
                                </TextBlock>
                            </section>
                        )}
                        {discounts && (
                            <section>
                                <Heading
                                    className="mb-2"
                                    headingLevel={3}
                                    appearance="h6"
                                >
                                    Discount Pass Policies
                                </Heading>
                                <TextBlock>
                                    <DisplayHtmlContent html={discounts} />
                                </TextBlock>
                            </section>
                        )}
                    </Stack>
                </AnchorPageSection>

                <Divider />

                <AnchorPageSection heading="Getting Here">
                    <FlexRow className="mt-2">
                        <FlexCol xs={4}>
                            <Heading headingLevel={3} appearance="h6">
                                GPS Coordinates
                            </Heading>
                        </FlexCol>
                        <FlexCol xs={8}>
                            <p>{`Latitude ${latitude}`}</p>
                            <p>{`Longitude ${longitude}`}</p>
                        </FlexCol>
                    </FlexRow>
                    <FlexRow className="mt-2">
                        <FlexCol xs={4}>
                            <Heading headingLevel={3} appearance="h6">
                                Driving Directions
                            </Heading>
                        </FlexCol>
                        <FlexCol xs={8}>
                            <TextBlock>
                                <DisplayHtmlContent html={drivingDirections} />
                            </TextBlock>
                        </FlexCol>
                    </FlexRow>
                </AnchorPageSection>

                <Divider />

                {addresses && (
                    <AnchorPageSection heading="Contact">
                        <FlexRow className="mt-2">
                            <FlexCol xs={4}>
                                <Heading
                                    headingLevel={3}
                                    appearance="h6"
                                    className="mb-2"
                                >
                                    Mailing Address
                                </Heading>
                            </FlexCol>
                            <FlexCol xs={8}>
                                {address.description1 && (
                                    <div>{address.description1}</div>
                                )}
                                {address.description2 && (
                                    <div>{address.description2}</div>
                                )}
                                {address.address1 && <div>{address.address1}</div>}
                                {address.address2 && <div>{address.address2}</div>}
                                {address3 && <div>{address3}</div>}
                            </FlexCol>
                        </FlexRow>
                        <FlexRow className="mt-2">
                            <FlexCol xs={4}>
                                <Heading
                                    headingLevel={3}
                                    appearance="h6"
                                    className="mb-2"
                                >
                                    Phone Number
                                </Heading>
                            </FlexCol>
                            <FlexCol xs={8}>
                                <p>{phone}</p>
                                {address.remarks && <div>{address.remarks}</div>}
                            </FlexCol>
                        </FlexRow>
                    </AnchorPageSection>
                )}

                <Divider />
                {video}
                <Divider />

                <AnchorPageSection heading="Map Information">
                    {map && (
                        <FlexRow hasGutters={false}>
                            <FlexCol className="rec-venue-page-map-wrapper">
                                <div id="map">{map}</div>
                            </FlexCol>
                        </FlexRow>
                    )}
                </AnchorPageSection>
            </Stack>
        </AnchorPageWithSidebarLayout>
    );
}

FacilityInfoTab.propTypes = {
    importantDates: PropTypes.array,
    addresses: PropTypes.array,
    permitDescription: PropTypes.object,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    phone: PropTypes.string,
    map: PropTypes.node,
    video: PropTypes.node,
};
